import { DateFormat } from '@aline/common';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInWeeks,
  format,
} from 'date-fns';
import type { Apollo } from 'src/gql/generated';

/**
 * Formats a date value for display in DataGrid cells.
 * Handles both Date objects and ISO string inputs.
 * Returns '-' for null/undefined values or invalid dates.
 *
 * @param dateValue - Date object, ISO string, or null/undefined
 * @param formatStr - Optional date-fns format string, defaults to 'MMM d, yyyy' (e.g., "Jan 1, 2024")
 * @returns Formatted date string or '-'
 */
export function formatDateForDisplay(
  dateValue: string | Date | null | undefined,
  formatStr: string = DateFormat.FULL_DATE_ABBREV_MONTH
): string {
  if (!dateValue) return '-';

  try {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '-';

    return format(date, formatStr);
  } catch (error) {
    console.warn('Invalid date value:', dateValue, error);
    return '-';
  }
}

/**
 * Formats a timestamp as a compact relative time for activity feeds — e.g. "now",
 * "5m ago", "3h ago", "2d ago", "3w ago". Falls back to an absolute date once the
 * value is older than ~a month. Matches the Figma "Updates" feed timestamp style.
 */
export function formatRelativeTime(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '';

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  // Anything under a minute is "now" — otherwise 45–59s floors to a misleading "0m ago".
  if (differenceInSeconds(now, date) < 60) return 'now';

  const minutes = differenceInMinutes(now, date);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = differenceInHours(now, date);
  if (hours < 24) return `${hours}h ago`;

  const days = differenceInDays(now, date);
  if (days < 7) return `${days}d ago`;

  const weeks = differenceInWeeks(now, date);
  if (weeks < 5) return `${weeks}w ago`;

  return format(date, DateFormat.FULL_DATE_ABBREV_MONTH);
}

export enum Direction {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum DateKey {
  createdAt = 'createdAt',
  dueDate = 'dueDate',
  updatedAt = 'updatedAt',
}

type DateObject<T> = Omit<T, DateKey> &
  Partial<Record<keyof typeof DateKey, Date | Apollo.Maybe<string>>>;

const defaultOptions = { dateKey: DateKey.dueDate, direction: Direction.Ascending };

/*
 * Given key 'createdAt' | 'updatedAt' | 'dueDate'
 * Returns comparator function for dateKey in direction on object
 * Defaults dueDate, Ascending
 * Returns (a, b) => number
 */
export function getDateComparator<T>(
  options: {
    dateKey?: DateKey;
    direction?: Direction;
  } = defaultOptions
): (a: DateObject<T>, b: DateObject<T>) => number {
  const { dateKey = DateKey.dueDate, direction = Direction.Ascending } = options || defaultOptions;

  return function (a: DateObject<T>, b: DateObject<T>): number {
    const valueA = a[dateKey];
    const valueB = b[dateKey];

    const timeA =
      valueA instanceof Date ? valueA.getTime() : valueA ? new Date(valueA).getTime() : 0;
    const timeB =
      valueB instanceof Date ? valueB.getTime() : valueB ? new Date(valueB).getTime() : 0;

    const diff = timeA - timeB;

    return direction === Direction.Ascending ? diff : -diff;
  };
}
